/**
 * Services Store using Zustand
 * Maintains identical state management structure from Mobile-client-core
 * Enhanced with service variations support
 */

import { create } from 'zustand';
import { calculateFinalPrice, calculateFinalDuration } from '../services/serviceVariationsService';

// Helper function to expand multiple choice variations into separate service instances
const expandMultipleChoiceVariations = (service, variations) => {
  const expandedServices = [];
  
  // Find multiple choice variations (arrays)
  const multipleChoiceGroups = Object.entries(variations).filter(([groupId, variation]) => Array.isArray(variation));
  const singleChoiceGroups = Object.entries(variations).filter(([groupId, variation]) => !Array.isArray(variation) && variation);
  
  if (multipleChoiceGroups.length === 0) {
    // No multiple choice variations - return single service instance
    const finalPrice = calculateFinalPrice(parseFloat(service.price), variations);
    return [{
      ...service,
      originalId: service.id,
      finalPrice,
      variationName: Object.values(variations).filter(v => v).map(v => v.name).join(' + ') || '',
      selectedVariations: Object.values(variations).filter(v => v)
    }];
  }
  
  // For each multiple choice group, create separate service instances
  multipleChoiceGroups.forEach(([groupId, variationsArray]) => {
    variationsArray.forEach(variation => {
      // Create variation object with this specific variation plus any single choice variations
      const serviceVariations = {
        ...singleChoiceGroups.reduce((acc, [gId, v]) => ({ ...acc, [gId]: v }), {}),
        [groupId]: variation
      };
      
      const finalPrice = calculateFinalPrice(parseFloat(service.price), serviceVariations);
      const allVariations = Object.values(serviceVariations).filter(v => v);
      
      expandedServices.push({
        ...service,
        id: `${service.id}_${variation.id}`, // Unique ID
        originalId: service.id,
        finalPrice,
        variationName: allVariations.map(v => v.name).join(' + '),
        selectedVariations: allVariations
      });
    });
  });
  
  return expandedServices;
};

const useServicesStore = create((set, get) => ({
  selectedServices: [], // Will now contain service instances with specific variations
  serviceVariations: {}, // { serviceId: { groupId: variation } }
  
  addService: (service) => set((state) => ({
    selectedServices: [...state.selectedServices, service]
  })),

  // Add service with variation as a unique cart item
  // This allows the same service to be added multiple times with different variations
  addServiceWithVariation: (service, variation, groupId) => set((state) => {
    // Create a unique cart item ID using service ID and variation ID
    const cartItemId = variation ? `${service.id}_${variation.id}` : service.id;

    // Check if this exact combination already exists
    const existingIndex = state.selectedServices.findIndex(s => s.cartItemId === cartItemId);
    if (existingIndex >= 0) {
      // Already exists, don't add duplicate
      return state;
    }

    // Calculate final price and duration with variation
    const basePrice = parseFloat(service.price) || 0;
    const baseDuration = service.duration_minutes || 0;
    const priceDelta = variation?.price_delta || 0;
    const durationDelta = variation?.duration_delta || 0;

    const cartItem = {
      ...service,
      cartItemId, // Unique ID for cart operations
      originalServiceId: service.id, // Keep original for API calls
      price: basePrice + priceDelta,
      duration_minutes: baseDuration + durationDelta,
      selectedVariation: variation ? {
        id: variation.id,
        name: variation.name,
        groupId: groupId,
        price_delta: priceDelta,
        duration_delta: durationDelta
      } : null,
      variation_id: variation?.id || null
    };

    // Also store in serviceVariations for compatibility
    const newServiceVariations = { ...state.serviceVariations };
    if (variation && groupId) {
      newServiceVariations[cartItemId] = {
        [groupId]: variation
      };
    }

    return {
      selectedServices: [...state.selectedServices, cartItem],
      serviceVariations: newServiceVariations
    };
  }),
  
  removeService: (serviceId) => set((state) => {
    // Also remove variations for this service
    const newVariations = { ...state.serviceVariations };
    delete newVariations[serviceId];

    // Support both cartItemId and regular id for removal
    return {
      selectedServices: state.selectedServices.filter(s =>
        (s.cartItemId || s.id) !== serviceId
      ),
      serviceVariations: newVariations
    };
  }),
  
  toggleService: (service) => set((state) => {
    const isSelected = state.selectedServices.some(s => s.id === service.id);
    if (isSelected) {
      // Remove service and its variations
      const newVariations = { ...state.serviceVariations };
      delete newVariations[service.id];
      
      return {
        selectedServices: state.selectedServices.filter(s => s.id !== service.id),
        serviceVariations: newVariations
      };
    } else {
      return {
        selectedServices: [...state.selectedServices, service]
      };
    }
  }),
  
  clearServices: () => set({ 
    selectedServices: [],
    serviceVariations: {}
  }),
  
  // Variation management
  setServiceVariation: (serviceId, groupId, variation, isMultipleChoice = false) => set((state) => {
    const newServiceVariations = { ...state.serviceVariations };
    
    if (variation === null) {
      // Remove the variation selection completely
      if (newServiceVariations[serviceId]) {
        const serviceVars = { ...newServiceVariations[serviceId] };
        delete serviceVars[groupId];
        
        // If no variations left for this service, remove the service entry
        if (Object.keys(serviceVars).length === 0) {
          delete newServiceVariations[serviceId];
        } else {
          newServiceVariations[serviceId] = serviceVars;
        }
      }
    } else {
      // Set the variation selection
      newServiceVariations[serviceId] = {
        ...newServiceVariations[serviceId],
        [groupId]: isMultipleChoice ? [variation] : variation
      };
    }
    
    return {
      serviceVariations: newServiceVariations
    };
  }),

  // Toggle variation for multiple choice groups
  toggleServiceVariation: (serviceId, groupId, variation) => set((state) => {
    const currentGroupVariations = state.serviceVariations[serviceId]?.[groupId];
    
    if (!Array.isArray(currentGroupVariations)) {
      // Initialize as array with first variation
      return {
        serviceVariations: {
          ...state.serviceVariations,
          [serviceId]: {
            ...state.serviceVariations[serviceId],
            [groupId]: [variation]
          }
        }
      };
    }
    
    // Check if variation is already selected
    const isSelected = currentGroupVariations.some(v => v.id === variation.id);
    
    if (isSelected) {
      // Remove variation
      const newVariations = currentGroupVariations.filter(v => v.id !== variation.id);
      const updatedServiceVariations = {
        ...state.serviceVariations[serviceId],
        [groupId]: newVariations.length > 0 ? newVariations : undefined
      };
      
      // Check if this service has any variations left
      const hasAnyVariations = Object.values(updatedServiceVariations).some(v => v && (Array.isArray(v) ? v.length > 0 : true));
      
      if (!hasAnyVariations) {
        // No variations left - remove service from cart entirely
        const newServiceVariations = { ...state.serviceVariations };
        delete newServiceVariations[serviceId];
        
        return {
          selectedServices: state.selectedServices.filter(s => s.id !== serviceId),
          serviceVariations: newServiceVariations
        };
      }
      
      return {
        serviceVariations: {
          ...state.serviceVariations,
          [serviceId]: updatedServiceVariations
        }
      };
    } else {
      // Add variation
      return {
        serviceVariations: {
          ...state.serviceVariations,
          [serviceId]: {
            ...state.serviceVariations[serviceId],
            [groupId]: [...currentGroupVariations, variation]
          }
        }
      };
    }
  }),
  
  removeServiceVariation: (serviceId, groupId) => set((state) => {
    const serviceVars = { ...state.serviceVariations[serviceId] };
    delete serviceVars[groupId];
    
    return {
      serviceVariations: {
        ...state.serviceVariations,
        [serviceId]: serviceVars
      }
    };
  }),
  
  getServiceVariations: (serviceId) => {
    const state = get();

    // First check the serviceVariations store (legacy flow)
    if (state.serviceVariations[serviceId] && Object.keys(state.serviceVariations[serviceId]).length > 0) {
      return state.serviceVariations[serviceId];
    }

    // For services added via addServiceWithVariation, build from cart items
    // This fixes the mismatch where variations are stored under cartItemId but looked up by serviceId
    const cartItemsForService = state.selectedServices.filter(s =>
      (s.originalServiceId || s.id) === serviceId && s.selectedVariation
    );

    if (cartItemsForService.length === 0) {
      return {};
    }

    // Build a variations object from cart items (for UI display purposes)
    // For multiple variations from the same group, return an array so UI shows all as selected
    const result = {};
    cartItemsForService.forEach(item => {
      if (item.selectedVariation && item.selectedVariation.groupId) {
        const groupId = item.selectedVariation.groupId;
        if (result[groupId]) {
          // Already have a variation for this group - convert to array or add to existing array
          if (Array.isArray(result[groupId])) {
            result[groupId].push(item.selectedVariation);
          } else {
            result[groupId] = [result[groupId], item.selectedVariation];
          }
        } else {
          result[groupId] = item.selectedVariation;
        }
      }
    });

    return result;
  },
  
  // Calculate final price including variations
  getServiceFinalPrice: (service) => {
    const state = get();

    // For cart items with embedded variation, price is already calculated
    if (service.selectedVariation && service.cartItemId) {
      return parseFloat(service.price) || 0;
    }

    const basePrice = parseFloat(service.price) || 0;
    const variations = state.getServiceVariations(service.id);
    return calculateFinalPrice(basePrice, variations);
  },

  // Calculate final duration including variations
  getServiceFinalDuration: (service) => {
    const state = get();

    // For cart items with embedded variation, duration is already calculated
    if (service.selectedVariation && service.cartItemId) {
      return service.duration_minutes || 0;
    }

    const baseDuration = service.duration_minutes || 0;
    const variations = state.getServiceVariations(service.id);
    return calculateFinalDuration(baseDuration, variations);
  },
  
  // Get expanded services (each variation as separate service instance)
  getExpandedServices: () => {
    const state = get();
    const expandedServices = [];

    state.selectedServices.forEach(service => {
      // For cart items with embedded variation (created by addServiceWithVariation),
      // they are already in final form - just add them directly
      if (service.selectedVariation && service.cartItemId) {
        expandedServices.push({
          ...service,
          originalId: service.originalServiceId || service.id,
          finalPrice: parseFloat(service.price) || 0,
          variationName: service.selectedVariation.name || '',
          selectedVariations: service.selectedVariation ? [service.selectedVariation] : []
        });
        return;
      }

      // For legacy items, use the old expansion logic
      const variations = state.serviceVariations[service.id] || {};
      const serviceInstances = expandMultipleChoiceVariations(service, variations);
      expandedServices.push(...serviceInstances);
    });

    return expandedServices;
  },

  getTotalPrice: () => {
    const state = get();
    const expandedServices = state.getExpandedServices();
    return expandedServices.reduce((total, serviceInstance) => {
      // finalPrice is set in getExpandedServices for all items
      return total + (serviceInstance.finalPrice || parseFloat(serviceInstance.price) || 0);
    }, 0);
  },

  getTotalDuration: () => {
    const state = get();
    return state.getExpandedServices().reduce((total, serviceInstance) => {
      // For cart items with embedded variation, duration_minutes is already calculated
      if (serviceInstance.cartItemId && serviceInstance.selectedVariation) {
        return total + (serviceInstance.duration_minutes || 0);
      }
      // For legacy items, calculate from base + variations
      const baseDuration = serviceInstance.duration_minutes || 0;
      const variationsDuration = (serviceInstance.selectedVariations || []).reduce((sum, variation) => {
        return sum + (variation.duration_delta || 0);
      }, 0);
      return total + baseDuration + variationsDuration;
    }, 0);
  },

  // Get count of expanded services (for display purposes)
  getExpandedServicesCount: () => {
    const state = get();
    return state.getExpandedServices().length;
  }
}));

export default useServicesStore;